// Mock data for Tyrre Burks website
// This file contains all placeholder content that will be replaced with backend data

export const siteConfig = {
  name: "Tyrre Burks",
  tagline: "Ty the Mission",
  email: "tyrre@tyrreburks.com",
  socialLinks: {
    linkedin: "https://linkedin.com/in/tyrreburks",
    instagram: "https://instagram.com/tyrreburks",
    twitter: "https://twitter.com/tyrreburks"
  }
};

export const heroData = {
  headline: "ALIGN YOUR LIFE WITH YOUR MISSION",
  subheadline: "Speaker. Entrepreneur. Mission Architect.",
  description: "Most people live fragmented lives—pulled in a hundred directions, unclear on what matters. I help leaders integrate their purpose into every role they carry.",
  ctaPrimary: "Book Tyrre to Speak",
  ctaSecondary: "Discover Your Mission"
};

export const aboutData = {
  headline: "The Guide Behind the Mission",
  bio: `Tyrre Burks doesn't do motivation for motivation's sake. He's a strategist—someone who helps leaders see clearly, act intentionally, and build lives that actually work.

After years of building companies, advising organizations, and speaking to audiences across the country, Tyrre developed "Ty the Mission"—a framework for integrating purpose into the roles we carry, the needs we meet, and the gifts we've been given.

His work isn't about finding your "why." It's about living it—every day, in every decision.`,
  stats: [
    { value: "100+", label: "Speaking Engagements" },
    { value: "50K+", label: "Lives Impacted" },
    { value: "15+", label: "Years Experience" }
  ],
  philosophy: "Alignment isn't a destination. It's a discipline."
};

export const speakingData = {
  headline: "Speaking That Transforms",
  intro: "Tyrre doesn't deliver keynotes. He delivers clarity. Every engagement is designed to move audiences from fragmentation to focus, from scattered to strategic.",
  topics: [
    {
      title: "Why Clarity Multiplies Effort",
      description: "When people know why they're doing the work, the work compounds. Clarity turns ordinary effort into exponential results.",
      duration: "45-60 min keynote",
      ideal: "Leadership summits, corporate teams, organizational development"
    },
    {
      title: "The Discipline of Direction",
      description: "Discipline isn't about doing more—it's about doing what matters most. Mission gives discipline its direction.",
      duration: "Keynote + workshop option",
      ideal: "Executive teams, high-performers, entrepreneurship events"
    },
    {
      title: "Identity-Driven Leadership",
      description: "The strongest leaders don't lead from position or performance—they lead from conviction. Identity-rooted leaders create cultures, not just results.",
      duration: "60 min keynote + workshop option",
      ideal: "Leadership development programs, faith-based conferences, corporate retreats"
    }
  ],
  testimonials: [
    {
      quote: "Tyrre doesn't just inspire—he equips. Our team left with actual tools, not just feelings.",
      author: "Sarah Mitchell",
      role: "CEO, Nexus Leadership Group"
    },
    {
      quote: "The clarity we gained in one session would have taken us years to figure out on our own.",
      author: "Marcus Johnson",
      role: "Executive Pastor, Grace Community Church"
    },
    {
      quote: "Finally, a speaker who respects the audience's intelligence. Grounded, practical, transformative.",
      author: "Dr. Amanda Chen",
      role: "Director of Leadership Development, Fortune 500"
    }
  ],
  pastClients: [
    "Fortune 500 Companies",
    "University Leadership Programs",
    "Faith-Based Organizations",
    "Entrepreneurship Conferences",
    "Corporate Retreats"
  ]
};

export const tieTheMissionData = {
  headline: "TY THE MISSION",
  tagline: "Purpose isn't found. It's built.",
  intro: "The Ty the Mission framework helps you integrate your purpose into the roles you carry, the needs you meet, and the gifts you've been given. It's not about discovering some hidden calling—it's about building clarity from the life you already have.",
  framework: {
    roles: {
      title: "Roles",
      description: "The identities you carry—who you are at your core. Man, father, husband, Christian, leader. These define your context for living out your mission.",
      question: "What are your roles?"
    },
    needs: {
      title: "Needs",
      description: "What the world is crying out for—the gap you feel called to fill. The need you can't ignore, the problem you're compelled to address.",
      question: "What does the world need most that you can help create?"
    },
    gifts: {
      title: "Gifts",
      description: "The unique abilities and strengths you've been given—forged through experience, refined through struggle, ready to be deployed.",
      question: "What gifts have you been given to fulfill that need?"
    }
  },
  cta: "Discover Your Mission Statement"
};

export const missionBuilderSteps = [
  {
    id: 1,
    title: "Your Roles",
    description: "Who are you? List the identities you carry.",
    fields: [
      { name: "roles", type: "textarea", label: "What are your roles?", placeholder: "Example: I'm a man, a father, a husband, a Christian, a leader..." }
    ]
  },
  {
    id: 2,
    title: "World's Need",
    description: "What is the world crying out for that you can help create?",
    fields: [
      { name: "worldNeed", type: "textarea", label: "What does the world need most that you can help create?", placeholder: "Example: The world needs love, acceptance, and safety..." }
    ]
  },
  {
    id: 3,
    title: "Your Gifts",
    description: "What unique abilities have you been given to fulfill that need?",
    fields: [
      { name: "gifts", type: "textarea", label: "What gifts have you been given to fulfill that need in the world?", placeholder: "Example: Embracing adversity with humility, seeking knowledge and responding with wisdom..." }
    ]
  }
];

export const bookingData = {
  headline: "Book Tyrre to Speak",
  intro: "Ready to bring clarity and alignment to your audience? Let's start a conversation about how Tyrre can serve your event.",
  formFields: [
    { name: "name", type: "text", label: "Your Name", required: true },
    { name: "email", type: "email", label: "Email Address", required: true },
    { name: "organization", type: "text", label: "Organization", required: true },
    { name: "eventType", type: "select", label: "Event Type", options: ["Corporate Conference", "Leadership Summit", "Faith-Based Event", "University/College", "Retreat/Workshop", "Other"], required: true },
    { name: "eventDate", type: "date", label: "Tentative Event Date", required: false },
    { name: "audienceSize", type: "select", label: "Expected Audience Size", options: ["Under 100", "100-500", "500-1000", "1000+"], required: true },
    { name: "message", type: "textarea", label: "Tell us about your event and what you're hoping to achieve", required: true }
  ],
  note: "Tyrre's team typically responds within 48 hours. For urgent inquiries, please note that in your message."
};

export const images = {
  hero: "https://customer-assets.emergentagent.com/job_1e5c107d-48e5-49a9-bc86-236f456dfd6e/artifacts/gtbq1kbg_IMG_5920.jpg",
  about: "https://customer-assets.emergentagent.com/job_1e5c107d-48e5-49a9-bc86-236f456dfd6e/artifacts/b90ua725_IMG_6237.jpg",
  speaking1: "https://customer-assets.emergentagent.com/job_1e5c107d-48e5-49a9-bc86-236f456dfd6e/artifacts/hg09aypb_Screenshot%202026-01-23%20at%2010.48.05%E2%80%AFPM.png",
  speaking2: "https://customer-assets.emergentagent.com/job_1e5c107d-48e5-49a9-bc86-236f456dfd6e/artifacts/hg09aypb_Screenshot%202026-01-23%20at%2010.48.05%E2%80%AFPM.png"
};